/**
 * Purchase limit on an order line (DEV-11416 / DEV-8783).
 *
 * `purchaseLimit` is counted in units of the product, per order and per variant: the
 * one-time line and the subscription line of the same variant share it, and a pack of
 * N spends N of it per unit chosen. The card already caps its dropdown this way; this
 * is the same rule for the surfaces that edit a line already on the order.
 */

/** A line as the order payload carries it, reduced to what the limit needs. */
export interface PurchaseLimitLine {
  variantId?: number | null;
  variant?: { id?: number | null, variantId?: number | null } | null;
  quantity?: number | null;
  purchaseLimit?: number | null;
  package?: { quantity?: number | null } | null;
}

/** The order's lines, one-time and subscription. */
export interface PurchaseLimitOrder {
  products?: { common?: PurchaseLimitLine[] | null, subscription?: PurchaseLimitLine[] | null } | null;
}

const variantIdOf = (line: PurchaseLimitLine) => line.variantId ?? line.variant?.variantId ?? line.variant?.id ?? null;

/**
 * The highest quantity the line can be set to under its purchase limit, or null when the
 * product has no limit. It never goes below what the line already holds, so a line that
 * was over the limit before it was set can still be lowered but not raised.
 */
export function getPurchaseLimitCeiling(line: PurchaseLimitLine | null | undefined, order: PurchaseLimitOrder | null | undefined): number | null {
  const purchaseLimit = line?.purchaseLimit || 0;
  if (!line || !purchaseLimit) return null;

  const packQuantity = line.package?.quantity || 1;
  const limit = Math.floor(purchaseLimit / packQuantity);
  const lineQuantity = line.quantity || 0;

  const variantId = variantIdOf(line);
  const orderLines = [...(order?.products?.common || []), ...(order?.products?.subscription || [])];
  const inOrder = variantId === null
    ? lineQuantity
    : orderLines
      .filter(l => variantIdOf(l) === variantId)
      .reduce((total, l) => total + (l.quantity || 0), 0);

  // The line itself may not be on `order` yet (a stale signal): count it at least once.
  const others = Math.max(0, inOrder - lineQuantity);

  return Math.max(lineQuantity, limit - others);
}
